import Immutable from 'immutable';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';

import { trackAnalyticsEvent } from 'app/actions/AnalyticsGa4Actions';
import { BackToLinkManager } from 'app/components/BackToLinkManager';
import ErrorPage from 'app/components/ErrorPage';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import ProgressBar from 'app/components/shared/ProgressBar';
import { eventNames } from 'app/constants/AnalyticsGa4Constants';
import { BROWSE_HOME_PAGE_PATH } from 'app/constants/BrowseConstants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { expandCategoryPath } from 'app/helpers/browse/categories';
import useFeature from 'app/hooks/useFeature';
import { selectCategoryConfig, selectIsFetching } from 'app/selectors/browse/browseCategorySelector';
import BrowseCategoryPage from './BrowseCategoryPage';
import BrowseCategoryLeafPageContainer from './BrowseCategoryLeafPageContainer';
import BrowseCategoryNodePageContainer from './BrowseCategoryNodePageContainer';
import BrowseCategoryNodePageLoadingStencil from './BrowseCategoryNodePageLoadingStencil';

const BrowseCategoryPageContainer = () => {
  const intl = useIntl();

  const baseUrl = useSelector(state => state.getIn(['app', 'baseURL']));
  const dispatch = useDispatch();
  const isFetching = useSelector(selectIsFetching);
  const browseCategoryConfig = useSelector(selectCategoryConfig);

  const [isShowStencil, setIsShowStencil] = useState(false);

  const categoryPath = browseCategoryConfig?.get('categoryPath', '') ?? '';
  const categoryByPath = browseCategoryConfig?.get('categoryByPath', Immutable.Map()) ?? Immutable.Map();

  useEffect(() => {
    let timer;
    if (isFetching) {
      timer = setTimeout(() => setIsShowStencil(true), 200);
    } else {
      setIsShowStencil(false);
    }
    return () => clearTimeout(timer);
  }, [isFetching]);

  useEffect(() => {
    if (!categoryPath) {
      return;
    }

    const paths = expandCategoryPath(categoryPath);

    dispatch(
      trackAnalyticsEvent({
        entities: {
          browse: {
            browse_category: categoryByPath.getIn([paths[0], 'title']),
            browse_category_1: categoryByPath.getIn([paths[1], 'title']),
            browse_category_2: categoryByPath.getIn([paths[2], 'title'])
          }
        },
        eventName: eventNames.PAGE_VIEW
      })
    );
  }, [categoryPath, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCoreV2BrowsePageEnabled = useFeature(TOGGLZ_FEATURES.CORE_V2_BROWSE_PAGE);
  if (!isCoreV2BrowsePageEnabled) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const category = categoryByPath.get(categoryPath)?.toJS();
  if (!(isFetching || category)) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const { subCategoryPaths = [], title: categoryTitle } = category || {};

  const backToPageTitle = categoryTitle || intl.formatMessage({ id: 'browse' });
  const backToPageScreenReaderLabel = intl.formatMessage(
    {
      defaultMessage: 'Back to {pageTitle}',
      id: 'sr_back_to_page_title'
    },
    {
      pageTitle: backToPageTitle
    }
  );

  const isCategoryLeaf = Boolean(category) && subCategoryPaths.length === 0;
  let pageDescription = '';
  if (isCategoryLeaf) {
    pageDescription = intl.formatMessage(
      {
        defaultMessage: 'Browse titles in {categoryTitle}.',
        id: 'browse.category.leaf_page_description'
      },
      {
        categoryTitle
      }
    );
  } else {
    const maxSubCategoryTitlesInDescription = 3;
    const allSubCategoryTitles = subCategoryPaths.map(subCategoryPath => {
      return categoryByPath.getIn([subCategoryPath, 'title']);
    });
    const truncatedSubCategoryTitles = allSubCategoryTitles.slice(0, maxSubCategoryTitlesInDescription);

    pageDescription = intl.formatMessage(
      {
        defaultMessage:
          'Browse titles by {categoryTitle} including {subCategoryTitles}{hasMore, select, true {, and more} other {}}.',
        id: 'browse.category.node_page_description'
      },
      {
        categoryTitle,
        subCategoryTitles: truncatedSubCategoryTitles.join(', '),
        hasMore: allSubCategoryTitles.length > truncatedSubCategoryTitles.length
      }
    );
  }

  const pageTitle = intl.formatMessage(
    {
      defaultMessage: 'Browse by {categoryTitle}',
      id: 'browse.category.page_title'
    },
    {
      categoryTitle
    }
  );

  const categoryPaths = expandCategoryPath(categoryPath);
  const breadcrumbLinkedDataItems = categoryPaths
    .map((breadcrumbPath, index) => {
      const breadcrumbCategory = categoryByPath.get(breadcrumbPath);
      if (!breadcrumbCategory) {
        return null;
      }

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': ''.concat(baseUrl, BROWSE_HOME_PAGE_PATH, breadcrumbCategory.get('path')),
          name: breadcrumbCategory.get('title')
        }
      };
    })
    .filter(Boolean);

  const linkedData = Immutable.fromJS({
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbLinkedDataItems
  });

  const breadcrumbLinks = [
    {
      label: intl.formatMessage({ id: 'browse' }),
      url: BROWSE_HOME_PAGE_PATH
    }
  ].concat(
    categoryPaths
      .map(path => {
        const breadcrumbCategory = categoryByPath.get(path);
        if (!breadcrumbCategory) {
          return null;
        }

        return {
          label: breadcrumbCategory.get('title'),
          url: BROWSE_HOME_PAGE_PATH.concat(breadcrumbCategory.get('path'))
        };
      })
      .filter(Boolean)
      .slice(-4)
  );

  return (
    <div style={{ minHeight: '480px' }}>
      <ProgressBar active={isFetching} />
      <CSSTransition classNames="fade" exit={false} in={isShowStencil} timeout={500} unmountOnExit>
        <BrowseCategoryNodePageLoadingStencil />
      </CSSTransition>

      {category && (
        <>
          <DocumentHeadContainer description={pageDescription} linkedData={linkedData} pageTitle={pageTitle} />
          <BackToLinkManager
            label={backToPageTitle}
            pageId="BrowseCategoryPage"
            screenReaderLabel={backToPageScreenReaderLabel}
          />
          <BrowseCategoryPage breadcrumbLinks={breadcrumbLinks} pageTitle={categoryTitle}>
            {isCategoryLeaf ? (
              <BrowseCategoryLeafPageContainer browseCategoryConfig={browseCategoryConfig} />
            ) : (
              <BrowseCategoryNodePageContainer browseCategoryConfig={browseCategoryConfig} />
            )}
          </BrowseCategoryPage>
        </>
      )}
    </div>
  );
};

export default BrowseCategoryPageContainer;
